import React, { lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Skeleton,
  Card,
  CardContent,
  Paper,
  ButtonBase,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import { TABS } from './constants';
import { setActiveTab } from '../../../redux/d2cAllocationSlice';

const TAB_COMPONENTS = {
  merchant: lazy(() => import('./tabs/MerchantDetailsTab')),
  rates:    lazy(() => import('./tabs/RateCardsTab')),
  forward:  lazy(() => import('./tabs/ForwardAllocationTab')),
  reverse:  lazy(() => import('./tabs/ReverseAllocationTab')),
  preview:  lazy(() => import('./tabs/PreviewPublishTab')),
  accounts: lazy(() => import('./tabs/AccountCodesTab')),
  log:      lazy(() => import('./tabs/ChangeLogTab')),
};

function TabFallback() {
  return (
    <Card variant="outlined">
      <CardContent>
        <Skeleton variant="text" width="40%" height={28} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 1 }} />
      </CardContent>
    </Card>
  );
}

function StepCircle({ step, isActive, isCompleted }) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all .2s',
        ...(isActive && {
          bgcolor: 'primary.main',
          boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.18)}`,
        }),
        ...(isCompleted && !isActive && { bgcolor: 'success.main' }),
        ...(!isActive && !isCompleted && {
          bgcolor: (t) => alpha(t.palette.grey[400], 0.22),
        }),
      }}
    >
      {isCompleted && !isActive ? (
        <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
      ) : (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: isActive ? '#fff' : 'text.secondary', lineHeight: 1 }}>
          {step}
        </Typography>
      )}
    </Box>
  );
}

function Connector({ active }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 2,
        flexShrink: 0,
        borderRadius: 1,
        bgcolor: active ? 'success.main' : (t) => alpha(t.palette.grey[400], 0.28),
        transition: 'background-color .2s',
      }}
    />
  );
}

export default function D2CAllocationEdit() {
  const dispatch  = useDispatch();
  const activeTab = useSelector((s) => s.d2cAllocation.activeTab);

  const editTabs   = TABS.filter((t) => t.id !== 'listing');
  const stepTabs   = editTabs.filter((t) => t.step);
  const utilityTab = editTabs.find((t) => t.utility);
  const ActiveComp = TAB_COMPONENTS[activeTab];

  const activeStep = stepTabs.find((t) => t.id === activeTab)?.step ?? 0;

  return (
    <Box>
      {/* ── Stepper ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          px: 2,
          py: 1.25,
          bgcolor: '#fff',
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          gap: 0,
        }}
      >
        {/* All tabs inline — step tabs + utility tab (Change Log) in one row */}
        <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {stepTabs.map((tab, idx) => {
            const isActive    = tab.id === activeTab;
            const isCompleted = tab.step < activeStep;
            return (
              <React.Fragment key={tab.id}>
                <ButtonBase
                  onClick={() => dispatch(setActiveTab(tab.id))}
                  sx={{
                    display: 'flex', flexDirection: 'row', alignItems: 'center',
                    gap: 0.75, px: 1, py: 0.75, borderRadius: 1.5, flexShrink: 0,
                    transition: 'background .15s',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.05) },
                  }}
                >
                  <StepCircle step={tab.step} isActive={isActive} isCompleted={isCompleted} />
                  <Typography sx={{ fontSize: 12, fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap',
                    color: isActive ? 'primary.main' : isCompleted ? 'success.dark' : 'text.secondary' }}>
                    {tab.label}
                  </Typography>
                </ButtonBase>
                <Connector active={isCompleted} />
              </React.Fragment>
            );
          })}

          {/* Change Log — inline, after the last connector */}
          {utilityTab && (
            <ButtonBase
              onClick={() => dispatch(setActiveTab(utilityTab.id))}
              sx={{
                display: 'flex', flexDirection: 'row', alignItems: 'center',
                gap: 0.75, px: 1, py: 0.75, borderRadius: 1.5, flexShrink: 0,
                transition: 'background .15s',
                '&:hover': { bgcolor: (t) => alpha(t.palette.grey[500], 0.08) },
              }}
            >
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                bgcolor: activeTab === utilityTab.id
                  ? (t) => alpha(t.palette.grey[600], 0.15)
                  : (t) => alpha(t.palette.grey[400], 0.15),
              }}>
                <HistoryIcon sx={{ fontSize: 14, color: activeTab === utilityTab.id ? 'text.primary' : 'text.disabled' }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: activeTab === utilityTab.id ? 700 : 500, whiteSpace: 'nowrap',
                color: activeTab === utilityTab.id ? 'text.primary' : 'text.secondary' }}>
                {utilityTab.label}
              </Typography>
            </ButtonBase>
          )}
        </Stack>
      </Paper>

      {/* Active tab content */}
      <Suspense fallback={<TabFallback />}>
        {ActiveComp ? <ActiveComp /> : <TabFallback />}
      </Suspense>
    </Box>
  );
}
